import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import api from "../api/axios";
import { FaRegSave } from "react-icons/fa";
import { useAuth } from "../auth/AuthContext";
import OwnerInfo from "./OwnerInfo";
import "./AddDealModal.css";
import UiText from './UiText';

// Zod validation schema
const dealSchema = z.object({
  title: z.string()
    .min(1, "Deal name is required")
    .min(2, "Please add a meaningful deal name (at least 2 characters)")
    .max(200, "Deal name cannot exceed 200 characters"),
  amount: z.string()
    .min(1, "Amount is required")
    .refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0, "Amount must be a positive number"),
  company: z.string()
    .min(1, "Company is required"),
  stage: z.string()
    .optional()
    .or(z.literal("")),
  category: z.enum(["regular", "federal"], {
    errorMap: () => ({ message: "Category is required" }),
  }),
  close_date: z.string()
    .refine(
      (val) => !val || new Date(val) > new Date(),
      "Close date must be in the future"
    )
    .optional()
    .or(z.literal("")),
  contact: z.string()
    .optional()
    .or(z.literal("")),
  priority: z.string()
    .optional()
    .or(z.literal("")),
  source: z.string()
    .optional()
    .or(z.literal("")),
  next_step: z.string()
    .max(200, "Next step cannot exceed 200 characters")
    .optional()
    .or(z.literal("")),
  description: z.string()
    .max(1000, "Description cannot exceed 1000 characters")
    .optional()
    .or(z.literal("")),
  collab_ids: z.array(z.string()).optional(),
});

// Stage options are loaded from the backend so admins can manage them
const PRIORITY_OPTIONS = ["low", "medium", "high"];
const SOURCE_OPTIONS = ["website", "email", "phone", "referral"];
const CATEGORY_OPTIONS = ["regular", "federal"];

const AddDealModal = ({ onClose, onSuccess }) => {
  const [companies, setCompanies] = useState([]);
  const [stages, setStages] = useState([]);
  const [apiError, setApiError] = useState("");
  const [users, setUsers] = useState([]);
  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(dealSchema),
    mode: "onChange",
    defaultValues: {
      title: "",
      amount: "",
      contact: "",
      company: "",
      collab_ids: [],
      stage: "prospecting",
      category: "",
      close_date: "",
      priority: "medium",
      source: "website",
      next_step: "",
      description: "",
    },
  });

  const formValues = watch();

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await api.get("/api/companies/", { params: { limit: 500 } });
        setCompanies(res.data);
      } catch (err) {
        console.error("Failed to load companies", err);
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await api.get('/api/admin/users/');
        setUsers(res.data || []);
      } catch (err) {
        // ignore - optional field
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const loadStages = async () => {
      try {
        const res = await api.get('/api/stages/');
        setStages(res.data || []);
        if (res.data && res.data.length > 0) {
          // set default stage to first stage key
          setValue('stage', res.data[0].key);
        }
      } catch (err) {
        console.error('Failed to load stages', err);
      }
    };
    loadStages();
  }, [setValue]);



  // Handle form submission
  const onSubmit = async (data) => {
    setApiError("");
    try {
      const payload = {
        title: data.title.trim(),
        amount: parseFloat(data.amount),
        company_id: data.company,
        collab_ids: Array.isArray(data.collab_ids) ? data.collab_ids : (data.collab_ids ? [data.collab_ids] : []),
        stage: data.stage || "prospecting",
        category: data.category,
        close_date: data.close_date || null,
        contact_ids: data.contact ? [data.contact] : [],
        priority: data.priority || "medium",
        source: data.source || "website",
        next_step: data.next_step?.trim() || null,
        description: data.description?.trim() || null,
      };

      await api.post("/api/deals/", payload);
      if (typeof onSuccess === "function") onSuccess();
      reset();
    } catch (err) {
      console.error(err);
      setApiError("Failed to create deal. Please try again.");
    }
  };

  // Contacts filtered by selected company
  const selectedCompany = companies.find((c) => String(c.id) === String(formValues.company));
  const filteredContacts = selectedCompany?.customers || [];

  const renderFormField = (label, fieldName, placeholder, isTextarea = false, maxLength = 100, isSelect = false, options = [], isRequired = false, disabled = false) => {
    const hasError = !!errors[fieldName];
    const fieldValue = formValues[fieldName] || "";

    return (
      <div key={fieldName}>
        <label className={`form-label ${hasError ? "label-error" : ""}`}>
          {label}
          {isRequired && " *"}
        </label>
        {isSelect ? (
          <select
            {...register(fieldName)}
            className={`form-select ${hasError ? "input-error" : ""}`}
            disabled={disabled}
          >
            <option value="">{`Select ${label}`}</option>
            {options.map((option) => {
              // For stages, prefer key over id; for others, prefer id
              const optionValue = option && typeof option === 'object' 
                ? (option.key || option.id) 
                : option;
              const optionDisplay = typeof option === 'object' 
                ? (option.label || option.name || option.email || String(option)) 
                : option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ');
              return (
                <option key={optionValue} value={optionValue}>
                  {optionDisplay}
                </option>
              );
            })}
          </select>
        ) : isTextarea ? (
          <textarea
            {...register(fieldName)}
            placeholder={placeholder}
            rows={4}
            className={`form-textarea ${hasError ? "input-error" : ""}`}
          />
        ) : (
          <input
            {...register(fieldName)}
            type={fieldName === "amount" ? "number" : fieldName === "close_date" ? "date" : "text"}
            placeholder={placeholder}
            className={`form-input ${hasError ? "input-error" : ""}`}
            disabled={disabled}
          />
        )}
        {hasError && (
          <div className="field-error">{errors[fieldName].message}</div>
        )}
        {!isSelect && maxLength > 0 && (
          <div className="char-count">{fieldValue.length}/{maxLength}</div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-2xl w-[700px] max-h-[90vh] overflow-y-auto shadow-xl">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <UiText variant="title" as="h2">Add New Deal</UiText>
          <button className="text-gray-500 hover:text-black" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* OWNER INFO */}
        {user && <OwnerInfo user={user} />}

        {/* API ERROR */}
        {apiError && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            <UiText variant="small">{apiError}</UiText>
          </div>
        )}

        {/* FORM */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* FORM GRID */}
          <div className="grid grid-cols-2 gap-6">
            {renderFormField("Deal Name", "title", "Enter deal name", false, 200, false, [], true)}
            {renderFormField("Amount", "amount", "Enter deal amount", false, 0, false, [], true)}
            {renderFormField("Company", "company", "Select company", false, 0, true, companies, true)}
            {renderFormField("Contact", "contact", "Select contact", false, 0, true, filteredContacts, false, !formValues.company)}
            <div>
              <label className={`form-label`}>Collaborators</label>
              <select {...register("collab_ids")} className={`form-select`} multiple>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.username || u.email}</option>
                ))}
              </select>
              <small>Select one or more collaborators (Ctrl+Click or Cmd+Click)</small>
            </div>
            {renderFormField("Stage", "stage", "Select stage", false, 0, true, stages, false)}
            {renderFormField("Category", "category", "Select category", false, 0, true, CATEGORY_OPTIONS, true)}
            {renderFormField("Priority", "priority", "Select priority", false, 0, true, PRIORITY_OPTIONS, false)}
            {renderFormField("Source", "source", "Select source", false, 0, true, SOURCE_OPTIONS, false)}
          </div>

          {/* CLOSE DATE */}
          <div className="mt-6">
            {renderFormField("Expected Close Date", "close_date", "", false, 0, false)}
          </div>

          {/* NEXT STEP */}
          <div className="mt-6">
            {renderFormField("Next Step", "next_step", "e.g., Send follow-up email", false, 200, false)}
          </div>

          {/* DESCRIPTION */}
          <div className="mt-6">
            {renderFormField("Description", "description", "Details about the deal...", true, 1000, false)}
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FaRegSave />
              Save Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDealModal;
